import { type ReactNode } from 'react';
import { type usAutocompletePro } from 'smartystreets-javascript-sdk';
import { Paragraph } from '@hungryroot/ramen';
import {
  type AddressRequestBody,
  type Address as ServerAddress,
} from '../../address/main';
import styles from './utils.module.css';

export const PHONE_MAX_LENGTH = 14;
const PHONE_VALID_LENGTH = 10;
export const ADDRESS_PLACEHOLDER = 'Start typing your first line of address';

export const initialAddressSuggestion: usAutocompletePro.Suggestion = {
  streetLine: '',
  secondary: '',
  city: '',
  state: '',
  zipcode: '',
  entries: 0,
};

export interface Address {
  street?: string;
  apt?: string | null;
  city?: string;
  state?: string;
  zipcode?: string;
}

export interface AddressDetails {
  name?: string;
  phone?: string | null;
  note?: string | null;
  sms_opt_in?: boolean;
}

export type CustomerAddress = Address & AddressDetails;

export enum ErrorMessage {
  name = 'Enter full name',
  street = 'Enter valid address',
  city = 'Enter valid city',
  state = 'Select state',
  zipcode = 'Enter a valid zip code with 5 digits',
  phone = 'Enter a valid phone number',
  genericHeading = 'Address could not be validated',
  genericBody = 'Unfortunately the address you entered could not be validated. Please review your address and try again.',
  zipcodeHeading = 'Address is outside our delivery zone',
}

export interface ErrorFields {
  name?: ErrorMessage.name;
  street?: ErrorMessage.street;
  city?: ErrorMessage.city;
  state?: ErrorMessage.state;
  zipcode?: ErrorMessage.zipcode;
  phone?: ErrorMessage.phone;
  modal?: {
    heading: string;
    body: string | ReactNode;
  };
}

export function getAddressErrorMessage(e: unknown): {
  heading: string;
  body: string | ReactNode;
} {
  const cast = e as {
    msg_id?: string;
    detail?: string;
  };
  const { detail } = cast;

  if (detail?.includes('zip_code')) {
    return {
      heading: ErrorMessage.zipcodeHeading,
      body: (
        <Paragraph>
          Unfortunately the address you entered is outside our delivery zone.
          Read more about our supported areas{' '}
          <a
            href="https://support.hungryroot.com/hc/en-us/articles/115006359108-Where-does-Hungryroot-deliver-#:~:text=Hungryroot%20delivers%20to%20most%20zip,our%20service%20for%20your%20area"
            target="_blank"
            className={styles['error-link']}
          >
            here
          </a>
          . Hope to see you soon!
        </Paragraph>
      ),
    };
  }

  return {
    heading: ErrorMessage.genericHeading,
    body: ErrorMessage.genericBody,
  };
}

export function isZipcodeError(value: string) {
  const isValid = /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(value);

  const err = !isValid ? ErrorMessage.zipcode : undefined;
  return err;
}

export function isPhoneError(value: string) {
  const isValid = /(^\(\d{3}\) \d{3}-\d{4}$)/.test(value);

  const err = !isValid ? ErrorMessage.phone : undefined;
  return err;
}

export function getAddressSuggestions(
  smartyPrimaryQuery?: usAutocompletePro.Suggestion[],
  smartySecondaryQuery?: usAutocompletePro.Suggestion[]
) {
  if (smartySecondaryQuery) {
    return smartySecondaryQuery.slice(0, 4);
  } else if (smartyPrimaryQuery) {
    return smartyPrimaryQuery;
  }
  return [initialAddressSuggestion];
}

export function getDisplayAddress(
  addressState?: Address,
  serverAddress?: ServerAddress
) {
  if (addressState !== undefined) {
    return addressState;
  }
  if (serverAddress !== undefined) {
    return {
      street: serverAddress.address1,
      apt: serverAddress.address2,
      city: serverAddress.city,
      state: serverAddress.state,
      zipcode: serverAddress.zip_code,
    };
  }
  return;
}

export function getDisplayAddressDetails(
  addressDetailsState?: AddressDetails,
  serverAddress?: ServerAddress
) {
  if (addressDetailsState !== undefined) {
    return {
      ...addressDetailsState,
      phone: addressDetailsState.phone || '',
      sms_opt_in: addressDetailsState.sms_opt_in ?? true,
    };
  }
  if (serverAddress !== undefined) {
    return {
      name: serverAddress.name,
      note: serverAddress.note,
      phone: serverAddress.phone,
      sms_opt_in: true,
    };
  }
  return;
}

export function validateRequiredFields(
  address: CustomerAddress | undefined
): ErrorFields | null {
  if (address === undefined) {
    return null;
  }

  const errors = Object.keys(address).reduce(
    (acc, key) => {
      let err;

      if (key === 'zipcode') {
        err =
          address.zipcode === undefined ||
          address.zipcode === '' ||
          isZipcodeError(address.zipcode)
            ? ErrorMessage.zipcode
            : undefined;
      } else if (key === 'phone') {
        const sanitizedPhone = address.phone && stripChars(address.phone);
        err =
          sanitizedPhone?.length === PHONE_VALID_LENGTH
            ? undefined
            : ErrorMessage.phone;
      } else {
        err =
          address[key as keyof typeof address] === undefined ||
          address[key as keyof typeof address] === ''
            ? ErrorMessage[key as keyof typeof ErrorMessage]
            : undefined;
      }

      if (err) {
        acc[key] = err;
      }
      return acc;
    },
    {} as Record<string, string | undefined>
  );

  return Object.keys(errors).length ? errors : null;
}

function stripChars(str: string) {
  return str.replace(/[^A-Z0-9]/gi, '');
}

type AddressObj = Address & AddressDetails;

export function getAddressRequestBody(address: AddressObj): AddressRequestBody {
  const phone = address?.phone ? stripChars(address.phone) : '';
  return {
    address1: address.street || '',
    address2: address.apt || '',
    city: address.city || '',
    company: '',
    is_commercial: false,
    name: address.name || '',
    note: address.note || '',
    order_id: '',
    phone,
    sms_opt_in: address?.sms_opt_in === true,
    state: address.state || '',
    zip_code: address.zipcode || '',
  };
}
